/**
 * @file UserAccountList.js
 * @description This page displays a list of user accounts with their respective details such as last name, first name, username,...
 */
import '../page-list/PageList.css';
import styles from './UserAccount.module.scss';
import { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { getDateFromZulu } from '../helpers/getDateFromZulu';
import {
    FilterModal,
    useFilter,
    renderFilterIcon,
} from '../helpers/FilterComponent';
import {
    fetchAdminUsers,
    deleteAdminUserById,
} from '../../services/systemService';
import AlertBox from '../ui/no_data_indicators/AlertBox';
import { LogEventType } from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import { ROUTES } from '../../constants/routes';
import { SecurityAttributes } from '../../utils/data/enums';
import { useAuthContext } from '../../store/Contexts';
import { getText } from '../../i18n';

const SortDirections = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const UserAccountList = () => {
    const navigate = useNavigate();
    const [userAccounts, setUserAccounts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isUnauthorized, setUnauthorized] = useState(false);
    const { showModal } = useContext(ModalContext);
    const [isLoading, setIsLoading] = useState(true);
    const [rowsClicked, setRowsClicked] = useState([]);
    const [expandedAgencyRows, setExpandedAgencyRows] = useState({});
    const [sortConfig, setSortConfig] = useState({
        key: 'lastName',
        direction: SortDirections.ASCENDING,
    });
    const [baseFilteredData, setBaseFilteredData] = useState([]);
    const [filteredData, applyFilter, clearAllFilters] =
        useFilter(baseFilteredData);
    const [activeModal, setActiveModal] = useState(null);
    const [selections, setSelections] = useState({});
    const { hasSecurityAttribute } = useAuthContext();

    useEffect(() => {
        const lowerSearchTerm = searchTerm.toLowerCase();
        const searchFiltered = userAccounts.filter(userAccount => {
            const normalizedLastLogin =
                getDateFromZulu(userAccount.lastActiveDate) || '-';
            return (
                userAccount.firstName
                    ?.toLowerCase()
                    .includes(lowerSearchTerm) ||
                userAccount.lastName?.toLowerCase().includes(lowerSearchTerm) ||
                userAccount.emailAddress
                    ?.toLowerCase()
                    .includes(lowerSearchTerm) ||
                userAccount.agencyName
                    ?.toLowerCase()
                    .includes(lowerSearchTerm) ||
                userAccount.status?.toLowerCase().includes(lowerSearchTerm) ||
                normalizedLastLogin.toLowerCase().includes(lowerSearchTerm) ||
                userAccount.lastActiveDate
                    ?.toLowerCase()
                    .includes(lowerSearchTerm)
            );
        });
        setBaseFilteredData(searchFiltered);
    }, [searchTerm, userAccounts]);

    const openModal = column => {
        setActiveModal(column);
    };
    const closeModal = () => {
        setActiveModal(null);
    };
    const handleApply = (column, selectedOptions) => {
        setSelections(prev => ({ ...prev, [column]: selectedOptions }));
        applyFilter(column, selectedOptions);
    };
    const clearFilters = () => {
        setSelections({});
        setSearchTerm('');
        clearAllFilters();
        //Object.keys(selections).forEach(column => applyFilter(column, []));
    };
    // Compute available options considering active filters for current modal
    const getOptions = column => {
        // Remove the filter effect of the current column from the selections state
        const filtersWithoutCurrent = { ...selections };
        delete filtersWithoutCurrent[column]; // Remove the current column's filter

        // Use these modified filters to filter data
        const filteredData = userAccounts.filter(userAccount =>
            Object.entries(filtersWithoutCurrent).every(
                ([key, values]) =>
                    values.length === 0 || values.includes(userAccount[key]),
            ),
        );

        // Now get all unique values for the current column from this filtered dataset
        return Array.from(new Set(filteredData.map(item => item[column])));
    };

    useEffect(() => {
        // TODO: Load real data for user accounts
        loadUserAccounts();
    }, []);

    const loadUserAccounts = async () => {
        try {
            const response = await fetchAdminUsers({});
            const processedResponse = response.map(item => {
                const names = item.agencyNames || [];
                return {
                    ...item,
                    status: getStatusString(item.tenantUserStatusId),
                    agencyName: names.join(', ') || '-',
                    agencyNames: names,
                };
            });

            setUserAccounts(processedResponse);

            setBaseFilteredData(processedResponse);
            setIsLoading(false);
        } catch (e) {
            if (e.status === 400) {
                setUnauthorized(true);
            }
            Toast.error(getText('msg_bd4f18854df8'));
        } finally {
            setIsLoading(false);
        }
    };

    const getStatusString = status => {
        switch (status) {
            case 1:
                return 'Pending';
            case 2:
                return 'Not Started';
            case 3:
                return 'Active';
            case 4:
                return 'Inactive';
            case 5:
                return 'Disabled';
            case 6:
                return 'Locked';
            default:
                return 'Undefined';
        }
    };

    const getStatusClass = status => {
        switch (status) {
            case 1:
                return 'ing-badge-pending';
            case 2:
                return 'ing-badge-inv-sent';
            case 3:
                return 'ing-badge-active';
            case 4:
                return 'ing-badge-inactive';
            case 5:
                return 'ing-badge-disabled';
            case 6:
                return 'ing-badge-error';
            default:
                return 'ing-badge-inactive';
        }
    };
    const sortedUserAccounts = useMemo(() => {
        return [...filteredData].sort((a, b) => {
            let aValue;
            let bValue;
            if (sortConfig.key === 'tenantUserStatusId') {
                aValue = getStatusString(a[sortConfig.key]);
                bValue = getStatusString(b[sortConfig.key]);
            } else if (sortConfig.key === 'agencyName') {
                aValue = String(a[sortConfig.key]);
                bValue = String(b[sortConfig.key]);
            } else if (sortConfig.key === 'lastActiveDate') {
                aValue = String(a[sortConfig.key]);
                bValue = String(b[sortConfig.key]);
            } else {
                aValue = a[sortConfig.key];
                bValue = b[sortConfig.key];
            }
            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();
            if (aValue < bValue) {
                return sortConfig.direction === SortDirections.ASCENDING
                    ? -1
                    : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === SortDirections.ASCENDING
                    ? 1
                    : -1;
            }
            return 0;
        });
    }, [filteredData, sortConfig]);

    const handleSearch = event => {
        setSearchTerm(event.target.value);
    };

    let handleSortLastName = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'lastName',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'lastName',
                direction: SortDirections.ASCENDING,
            });
        }
    };
    let handleSortFirstName = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'firstName',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'firstName',
                direction: SortDirections.ASCENDING,
            });
        }
    };
    let handleSortEmailAddress = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'emailAddress',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'emailAddress',
                direction: SortDirections.ASCENDING,
            });
        }
    };
    let handleSortAgency = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'agencyName',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'agencyName',
                direction: SortDirections.ASCENDING,
            });
        }
    };
    let handleSortStatus = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'status',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'status',
                direction: SortDirections.ASCENDING,
            });
        }
    };
    let handleSortLastActive = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'lastActiveDate',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'lastActiveDate',
                direction: SortDirections.ASCENDING,
            });
        }
    };
    const handlerRowClicked = id => {
        let newRowsClicked = rowsClicked;
        if (!rowsClicked.includes(id)) {
            newRowsClicked.push(id);
        } else {
            newRowsClicked.splice(newRowsClicked.indexOf(id), 1);
        }
        setRowsClicked([...newRowsClicked]);
    };

    const deleteUserAccounts = () => {
        rowsClicked.map(row => {
            userAccounts.filter((userAccount, index) => {
                if (userAccount.id === row) {
                    deleteAdminUserById(userAccount.id).then(response => {
                        Toast.success(
                            "User with email '" +
                                userAccount.emailAddress +
                                "' was deleted successfully.",
                        );
                        deselectRows(rowsClicked);
                        loadUserAccounts();
                    });
                }
                return null;
            });
            return null;
        });
    };

    const deselectRows = rowsClicked => {
        rowsClicked.map(row => {
            userAccounts.filter(userAccount => {
                if (userAccount.id === row) {
                    document.getElementById(userAccount.id).checked = false;
                }
                return null;
            });
            return null;
        });
        setRowsClicked([]);
    };

    const toggleAgencyExpand = userId => {
        setExpandedAgencyRows(prev => ({
            ...prev,
            [userId]: !prev[userId],
        }));
    };

    const getTruncatedAgencyLabel = (names, maxChars = 30) => {
        if (names.length === 0) return '-';
        let label = names[0];
        let shown = 1;

        for (let i = 1; i < names.length; i++) {
            const remaining = names.length - shown;
            const suffix = ` +${remaining} more`;
            const candidate = `${label}, ${names[i]}`;
            const wouldOverflow =
                i < names.length - 1
                    ? candidate.length +
                          ` +${names.length - shown - 1} more`.length >
                      maxChars
                    : candidate.length > maxChars;

            if (wouldOverflow) {
                return `${label}${suffix}`;
            }
            label = candidate;
            shown++;
        }
        return label;
    };

    const renderAgencyCell = userAccount => {
        const names = userAccount.agencyNames || [];
        if (names.length === 0) return <span>-</span>;

        if (names.length === 1) {
            return (
                <span className="badge-base badge-population-group">
                    {names[0]}
                </span>
            );
        }

        const isExpanded = expandedAgencyRows[userAccount.id];

        return (
            <div
                className={styles.agencyAccordion}
                onClick={e => {
                    e.stopPropagation();
                    toggleAgencyExpand(userAccount.id);
                }}
            >
                {isExpanded ? (
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            flexDirection: 'row',
                            alignItems: 'flex-start',
                            gap: '6px',
                        }}
                    >
                        {names.map(name => (
                            <span
                                key={name}
                                className="badge-base badge-population-group"
                            >
                                {name}
                            </span>
                        ))}
                    </div>
                ) : (
                    <div style={{ whiteSpace: 'nowrap' }}>
                        <span className="badge-base badge-population-group">
                            {names[0]}
                        </span>
                        {names.length > 1 && (
                            <span
                                style={{
                                    fontSize: '12px',
                                    color: '#667085',
                                    marginLeft: '6px',
                                }}
                            >
                                {`+${names.length - 1} more`}
                            </span>
                        )}
                        <i
                            className="bi bi-chevron-down"
                            style={{
                                fontSize: '10px',
                                color: '#667085',
                                marginLeft: '6px',
                            }}
                        />
                    </div>
                )}
                {isExpanded && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            marginTop: '4px',
                        }}
                    >
                        <i
                            className="bi bi-chevron-up"
                            style={{ fontSize: '10px', color: '#667085' }}
                        />
                    </div>
                )}
            </div>
        );
    };

    const rowAction = action => {
        if (rowsClicked.length > 0) {
            switch (action) {
                case 'delete':
                    showModal({
                        type: ModalTypes.DELETE_CONFIRMATION,
                        title: getText('msg_697cc22f7856'),
                        text: getText('msg_443b801a3c92'),
                        onYes: () => deleteUserAccounts(),
                        onNo: () => {},
                        noButtonText: getText('msg_77dfd2135f4d'),
                        yesButtonText: getText('msg_f6fdbe48dc54'),
                    });
                    logEvent(
                        LogEventType.DeleteClicked,
                        `Users delete clicked`,
                        null,
                    );
                    break;
                default:
                    break;
            }
        } else {
            Toast.error(getText('msg_9bcf67dc54d5'));
        }
    };

    return (
        <div className="page-content">
            {isLoading ? (
                <Spinner width={50} className={styles.loadingSpinner} />
            ) : (
                <>
                    {isUnauthorized ? (
                        <>
                            <AlertBox
                                title={getText('msg_53cc4f43d8a7')}
                                description={getText('msg_7de2a9f3cc6c')}
                            />
                        </>
                    ) : (
                        <div className="card">
                            <div className="card-body px-0 py-4">
                                {/* Screen Header with Title and Actions */}
                                <div class="d-flex justify-content-between align-items-center mb-4">
                                    {/* Screen Title */}
                                    <div className="float-start d-flex align-items-center">
                                        <p
                                            className={`page-title card-title ms-4 me-5 ${styles.userListTitle}`}
                                        >
                                            {getText('msg_57f2b181d0a5')}
                                        </p>
                                        <div className={styles.searchWrapper}>
                                            <input
                                                type="text"
                                                className={styles.searchInput}
                                                placeholder={getText(
                                                    'msg_12d467dccd15',
                                                )}
                                                value={searchTerm}
                                                onChange={handleSearch}
                                            />
                                            <span className={styles.searchIcon}>
                                                <i className="bi bi-search"></i>
                                            </span>
                                        </div>
                                    </div>
                                    {/* Screen Actions */}
                                    <div className="float-end d-flex align-items-center">
                                        {hasSecurityAttribute([
                                            SecurityAttributes.UserAccountFullAccess,
                                        ]) && (
                                            <span
                                                type="button"
                                                onClick={() =>
                                                    rowAction('delete')
                                                }
                                                className="ing-text-buttons mx-3"
                                            >
                                                <i className="bi bi-trash3 me-2" />
                                                {getText('msg_f6fdbe48dc54')}
                                            </span>
                                        )}
                                        <span
                                            type="button"
                                            onClick={clearFilters}
                                            className="ing-text-buttons mx-3"
                                        >
                                            <i className="bi bi-arrow-counterclockwise me-2" />
                                            {getText('msg_381c4bf1d409')}
                                        </span>
                                        {hasSecurityAttribute([
                                            SecurityAttributes.UserAccountFullAccess,
                                        ]) && (
                                            <span
                                                onClick={() =>
                                                    navigate(
                                                        ROUTES.USER_ACCOUNTS_ADD,
                                                    )
                                                }
                                                type="button"
                                                className="ms-3 me-4 button-base ing-button-primary-left align-middle"
                                            >
                                                <i className="bi bi-plus-lg me-1" />
                                                {getText('msg_61cc55aa0453')}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="table-responsive-xxl w-100">
                                    {/* Template List Table */}
                                    <table className="table table-hover align-middle">
                                        <thead className="custom-table-header table-light">
                                            <tr>
                                                {hasSecurityAttribute([
                                                    SecurityAttributes.UserAccountFullAccess,
                                                ]) && (
                                                    <th className="text-center">
                                                        <svg
                                                            width="19"
                                                            height="19"
                                                            viewBox="0 0 19 19"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                d="M5.2 9.3H12.8"
                                                                stroke="#0070FF"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                            <rect
                                                                x="0.5"
                                                                y="0.5"
                                                                width="17"
                                                                height="17"
                                                                rx="4"
                                                                stroke="#0070FF"
                                                            />
                                                        </svg>
                                                    </th>
                                                )}
                                                <th>
                                                    <span>
                                                        {getText(
                                                            'msg_863cb39fbe7d',
                                                        )}
                                                    </span>
                                                    <span
                                                        onClick={
                                                            handleSortLastName
                                                        }
                                                        style={{
                                                            cursor: 'pointer',
                                                        }}
                                                        className="mx-2"
                                                    >
                                                        <svg
                                                            width={11}
                                                            height={6}
                                                            viewBox="0 0 11 6"
                                                            fill="none"
                                                        >
                                                            <path
                                                                transform={
                                                                    sortConfig.key ===
                                                                        'lastName' &&
                                                                    sortConfig.direction ===
                                                                        SortDirections.DESCENDING
                                                                        ? 'rotate(180 5.25 3)'
                                                                        : ''
                                                                }
                                                                d="M1 0.75L5.25 5.25L9.5 0.75"
                                                                stroke="#2F2B43"
                                                                strokeWidth="1.5"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </span>
                                                    <span
                                                        onClick={() =>
                                                            openModal(
                                                                'lastName',
                                                            )
                                                        }
                                                        className={
                                                            styles.cursorPointer
                                                        }
                                                    >
                                                        {renderFilterIcon(
                                                            selections[
                                                                'lastName'
                                                            ]?.length > 0,
                                                        )}
                                                    </span>
                                                    {activeModal ===
                                                        'lastName' && (
                                                        <FilterModal
                                                            column={activeModal}
                                                            options={getOptions(
                                                                activeModal,
                                                            )}
                                                            selectedOptions={
                                                                selections[
                                                                    activeModal
                                                                ] || []
                                                            }
                                                            //onSelectionChange={(option) => handleFilterChange(activeModal, option)}
                                                            //onSelectAll={handleSelectAll}
                                                            onApply={
                                                                handleApply
                                                            }
                                                            onClose={closeModal}
                                                        />
                                                    )}
                                                </th>
                                                <th>
                                                    <span>
                                                        {getText(
                                                            'msg_b6ea992aab46',
                                                        )}
                                                    </span>
                                                    <span
                                                        onClick={
                                                            handleSortFirstName
                                                        }
                                                        style={{
                                                            cursor: 'pointer',
                                                        }}
                                                        className="px-2"
                                                    >
                                                        <svg
                                                            width={11}
                                                            height={6}
                                                            viewBox="0 0 11 6"
                                                            fill="none"
                                                        >
                                                            <path
                                                                transform={
                                                                    sortConfig.key ===
                                                                        'firstName' &&
                                                                    sortConfig.direction ===
                                                                        SortDirections.DESCENDING
                                                                        ? 'rotate(180 5.25 3)'
                                                                        : ''
                                                                }
                                                                d="M1 0.75L5.25 5.25L9.5 0.75"
                                                                stroke="#2F2B43"
                                                                strokeWidth="1.5"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </span>
                                                    <span
                                                        onClick={() =>
                                                            openModal(
                                                                'firstName',
                                                            )
                                                        }
                                                        className={
                                                            styles.cursorPointer
                                                        }
                                                    >
                                                        {renderFilterIcon(
                                                            selections[
                                                                'firstName'
                                                            ]?.length > 0,
                                                        )}
                                                    </span>
                                                    {activeModal ===
                                                        'firstName' && (
                                                        <FilterModal
                                                            column={activeModal}
                                                            options={getOptions(
                                                                activeModal,
                                                            )}
                                                            selectedOptions={
                                                                selections[
                                                                    activeModal
                                                                ] || []
                                                            }
                                                            //onSelectionChange={(option) => handleFilterChange(activeModal, option)}
                                                            //onSelectAll={handleSelectAll}
                                                            onApply={
                                                                handleApply
                                                            }
                                                            onClose={closeModal}
                                                        />
                                                    )}
                                                </th>
                                                <th>
                                                    <span>
                                                        {getText(
                                                            'msg_09ba557fd106',
                                                        )}
                                                    </span>
                                                    <span
                                                        onClick={
                                                            handleSortEmailAddress
                                                        }
                                                        style={{
                                                            cursor: 'pointer',
                                                        }}
                                                        className="px-2"
                                                    >
                                                        <svg
                                                            width={11}
                                                            height={6}
                                                            viewBox="0 0 11 6"
                                                            fill="none"
                                                        >
                                                            <path
                                                                transform={
                                                                    sortConfig.key ===
                                                                        'emailAddress' &&
                                                                    sortConfig.direction ===
                                                                        SortDirections.DESCENDING
                                                                        ? 'rotate(180 5.25 3)'
                                                                        : ''
                                                                }
                                                                d="M1 0.75L5.25 5.25L9.5 0.75"
                                                                stroke="#2F2B43"
                                                                strokeWidth="1.5"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </span>
                                                    <span
                                                        onClick={() =>
                                                            openModal(
                                                                'emailAddress',
                                                            )
                                                        }
                                                        className={
                                                            styles.cursorPointer
                                                        }
                                                    >
                                                        {renderFilterIcon(
                                                            selections[
                                                                'emailAddress'
                                                            ]?.length > 0,
                                                        )}
                                                    </span>
                                                    {activeModal ===
                                                        'emailAddress' && (
                                                        <FilterModal
                                                            column={activeModal}
                                                            options={getOptions(
                                                                activeModal,
                                                            )}
                                                            selectedOptions={
                                                                selections[
                                                                    activeModal
                                                                ] || []
                                                            }
                                                            //onSelectionChange={(option) => handleFilterChange(activeModal, option)}
                                                            //onSelectAll={handleSelectAll}
                                                            onApply={
                                                                handleApply
                                                            }
                                                            onClose={closeModal}
                                                        />
                                                    )}
                                                </th>
                                                <th>
                                                    <span>
                                                        {getText(
                                                            'msg_5c47e26c6ad2',
                                                        )}
                                                    </span>
                                                    <span
                                                        onClick={
                                                            handleSortAgency
                                                        }
                                                        style={{
                                                            cursor: 'pointer',
                                                        }}
                                                        className="px-2"
                                                    >
                                                        <svg
                                                            width={11}
                                                            height={6}
                                                            viewBox="0 0 11 6"
                                                            fill="none"
                                                        >
                                                            <path
                                                                transform={
                                                                    sortConfig.key ===
                                                                        'agencyName' &&
                                                                    sortConfig.direction ===
                                                                        SortDirections.DESCENDING
                                                                        ? 'rotate(180 5.25 3)'
                                                                        : ''
                                                                }
                                                                d="M1 0.75L5.25 5.25L9.5 0.75"
                                                                stroke="#2F2B43"
                                                                strokeWidth="1.5"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </span>
                                                    <span
                                                        onClick={() =>
                                                            openModal(
                                                                'agencyName',
                                                            )
                                                        }
                                                        className={
                                                            styles.cursorPointer
                                                        }
                                                    >
                                                        {renderFilterIcon(
                                                            selections[
                                                                'agencyName'
                                                            ]?.length > 0,
                                                        )}
                                                    </span>
                                                    {activeModal ===
                                                        'agencyName' && (
                                                        <FilterModal
                                                            column={activeModal}
                                                            options={getOptions(
                                                                activeModal,
                                                            )}
                                                            selectedOptions={
                                                                selections[
                                                                    activeModal
                                                                ] || []
                                                            }
                                                            //onSelectionChange={(option) => handleFilterChange(activeModal, option)}
                                                            //onSelectAll={handleSelectAll}
                                                            onApply={
                                                                handleApply
                                                            }
                                                            onClose={closeModal}
                                                        />
                                                    )}
                                                </th>
                                                <th>
                                                    <span>
                                                        {getText(
                                                            'msg_6e702cb4e0c7',
                                                        )}
                                                    </span>
                                                    <span
                                                        onClick={
                                                            handleSortStatus
                                                        }
                                                        style={{
                                                            cursor: 'pointer',
                                                        }}
                                                        className="px-2"
                                                    >
                                                        <svg
                                                            width={11}
                                                            height={6}
                                                            viewBox="0 0 11 6"
                                                            fill="none"
                                                        >
                                                            <path
                                                                transform={
                                                                    sortConfig.key ===
                                                                        'status' &&
                                                                    sortConfig.direction ===
                                                                        SortDirections.DESCENDING
                                                                        ? 'rotate(180 5.25 3)'
                                                                        : ''
                                                                }
                                                                d="M1 0.75L5.25 5.25L9.5 0.75"
                                                                stroke="#2F2B43"
                                                                strokeWidth="1.5"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </span>
                                                    <span
                                                        onClick={() =>
                                                            openModal('status')
                                                        }
                                                        className={
                                                            styles.cursorPointer
                                                        }
                                                    >
                                                        {renderFilterIcon(
                                                            selections['status']
                                                                ?.length > 0,
                                                        )}
                                                    </span>
                                                    {activeModal ===
                                                        'status' && (
                                                        <FilterModal
                                                            column={activeModal}
                                                            options={getOptions(
                                                                activeModal,
                                                            )}
                                                            selectedOptions={
                                                                selections[
                                                                    activeModal
                                                                ] || []
                                                            }
                                                            //onSelectionChange={(option) => handleFilterChange(activeModal, option)}
                                                            //onSelectAll={handleSelectAll}
                                                            onApply={
                                                                handleApply
                                                            }
                                                            onClose={closeModal}
                                                            getString={
                                                                getStatusString
                                                            }
                                                        />
                                                    )}
                                                </th>
                                                <th>
                                                    <span>
                                                        {getText(
                                                            'msg_2df48ad223be',
                                                        )}
                                                    </span>
                                                    <span
                                                        onClick={
                                                            handleSortLastActive
                                                        }
                                                        style={{
                                                            cursor: 'pointer',
                                                        }}
                                                        className="px-2"
                                                    >
                                                        <svg
                                                            width={11}
                                                            height={6}
                                                            viewBox="0 0 11 6"
                                                            fill="none"
                                                        >
                                                            <path
                                                                transform={
                                                                    sortConfig.key ===
                                                                        'lastActiveDate' &&
                                                                    sortConfig.direction ===
                                                                        SortDirections.DESCENDING
                                                                        ? 'rotate(180 5.25 3)'
                                                                        : ''
                                                                }
                                                                d="M1 0.75L5.25 5.25L9.5 0.75"
                                                                stroke="#2F2B43"
                                                                strokeWidth="1.5"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </span>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedUserAccounts.map(
                                                (userAccount, index) => (
                                                    <tr
                                                        key={userAccount.id}
                                                        className={`${
                                                            rowsClicked.includes(
                                                                userAccount.id,
                                                            )
                                                                ? 'clicked-class'
                                                                : ''
                                                        }`}
                                                    >
                                                        {hasSecurityAttribute([
                                                            SecurityAttributes.UserAccountFullAccess,
                                                        ]) && (
                                                            <td className="text-center">
                                                                <input
                                                                    id={
                                                                        userAccount.id
                                                                    }
                                                                    onClick={() =>
                                                                        handlerRowClicked(
                                                                            userAccount.id,
                                                                        )
                                                                    }
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                />
                                                            </td>
                                                        )}
                                                        <td
                                                            className="titleCell"
                                                            onClick={() =>
                                                                navigate(
                                                                    `/user-accounts/view/${userAccount.id}`,
                                                                )
                                                            }
                                                        >
                                                            {
                                                                userAccount.lastName
                                                            }
                                                        </td>
                                                        <td
                                                            className="subtitleCell"
                                                            onClick={() =>
                                                                navigate(
                                                                    `/user-accounts/view/${userAccount.id}`,
                                                                )
                                                            }
                                                        >
                                                            {
                                                                userAccount.firstName
                                                            }
                                                        </td>
                                                        <td className="subtitleCell">
                                                            {
                                                                userAccount.emailAddress
                                                            }
                                                        </td>
                                                        <td className="item-page-changed-by">
                                                            {renderAgencyCell(
                                                                userAccount,
                                                            )}
                                                        </td>
                                                        <td className="item-page-changed-by">
                                                            <button
                                                                className={getStatusClass(
                                                                    userAccount.tenantUserStatusId,
                                                                )}
                                                            >
                                                                {getStatusString(
                                                                    userAccount.tenantUserStatusId,
                                                                )}
                                                            </button>
                                                        </td>
                                                        <td className="subtitleCell">
                                                            {getDateFromZulu(
                                                                userAccount.lastActiveDate,
                                                            ) || '-'}
                                                        </td>
                                                    </tr>
                                                ),
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                <p
                                    className="text-center fs-5"
                                    style={{
                                        margin: '60px 0',
                                        color: 'rgba(47, 43, 67, 0.6)',
                                    }}
                                >
                                    {userAccounts.length <= 0
                                        ? 'No userAccounts found'
                                        : ''}
                                </p>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
export default UserAccountList;
